import { useEffect, useMemo, useState } from 'react';
import '@/App.css';
import Editor from '@/pages/Editor';
import AdminMetrics from '@/pages/AdminMetrics';
import { Toaster } from '@/components/ui/sonner';
import { UnlockDialog } from '@/components/editor/UnlockDialog';
import { MobileBlock } from '@/components/MobileBlock';
import { ReadOnlyViewer } from '@/components/ReadOnlyViewer';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { api } from '@/lib/api';
import { sanitizeDocImages } from '@/lib/safeUri';
import { toast } from 'sonner';
import { ShieldCheck, Loader2 } from 'lucide-react';

// Branded full-screen backdrop shown on mobile behind the unlock prompt / while
// loading a shared document.
const MobileGate = ({ title, subtitle, loading }) => (
  <div className="flex min-h-[100dvh] w-full flex-col items-center justify-center gap-4 bg-[#0F1115] px-7 text-center text-white">
    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
      {loading ? <Loader2 className="h-7 w-7 animate-spin text-white/90" /> : <ShieldCheck className="h-7 w-7 text-[#34D399]" />}
    </div>
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6EA8FF]">SwitchR</p>
    <h1 className="text-lg font-semibold">{title}</h1>
    {subtitle && <p className="max-w-sm text-sm text-white/70">{subtitle}</p>}
  </div>
);

function App() {
  const { t } = useI18n();
  const isMobile = useIsMobile();
  const loadDoc = useStore((s) => s.loadDoc);

  const sid = useMemo(() => new URLSearchParams(window.location.search).get('p'), []);
  // Hidden admin metrics area at /sys-metrics (gated by pathname; no router).
  const isAdmin = useMemo(() => {
    try {
      return window.location.pathname.replace(/\/+$/, '').toLowerCase() === '/sys-metrics';
    } catch {
      return false;
    }
  }, []);
  // status: none | loading | ready | unlocked | failed
  const [status, setStatus] = useState(sid ? 'loading' : 'none');
  const [payload, setPayload] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);

  const stripShareParam = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('p');
      window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
    } catch (e) {}
  };

  useEffect(() => {
    if (!sid) return;
    api
      .get(`/shares/${sid}`)
      .then((r) => {
        if (r.data && r.data.ciphertext) {
          setPayload(r.data);
          setStatus('ready');
        } else {
          toast.error(t('share_link_invalid'));
          setStatus('failed');
          stripShareParam();
        }
      })
      .catch(() => {
        toast.error(t('share_link_invalid'));
        setStatus('failed');
        stripShareParam();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUnlock = (rawDoc, mode) => {
    // Shared payloads are untrusted input: strip non-image logo/background URIs.
    const doc = sanitizeDocImages(rawDoc);
    if (isMobile) {
      // Phones: read-only view only — never persist over local data.
      setViewerDoc(doc);
      setStatus('unlocked');
      toast.success(t('share_unlocked'));
      return;
    }
    if (mode === 'view') {
      // Desktop "view only" share: open the read-only viewer, do not load into
      // the editor or persist over local data.
      setViewerDoc(doc);
      setStatus('viewing');
      stripShareParam();
      toast.success(t('share_unlocked'));
    } else {
      loadDoc(doc);
      setStatus('unlocked');
      stripShareParam();
      toast.success(t('share_unlocked'));
    }
  };

  const onClose = () => {
    setPayload(null);
    setStatus(sid && isMobile ? 'failed' : 'none');
    stripShareParam();
  };

  // Cookieless, privacy-friendly page-view beacon. Fires once per page load.
  // Sends no personal data; the server derives an anonymous country + a daily
  // rotating hash and stores only aggregate counters. Skipped on the admin page.
  useEffect(() => {
    if (isAdmin) return;
    api.post('/track', { path: window.location.pathname }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Hidden admin metrics area ----
  if (isAdmin) {
    return (
      <>
        <AdminMetrics />
        <Toaster position="top-right" richColors closeButton />
      </>
    );
  }

  // ---- Mobile: editing is not supported; shared links are read-only ----
  if (isMobile) {
    if (status === 'unlocked' && viewerDoc) {
      return (
        <>
          <ReadOnlyViewer doc={viewerDoc} />
          <Toaster position="top-center" richColors closeButton />
        </>
      );
    }
    if (status === 'loading') {
      return (
        <>
          <MobileGate loading title={t('mobile_loading')} />
          <Toaster position="top-center" richColors closeButton />
        </>
      );
    }
    if (status === 'ready') {
      return (
        <>
          <MobileGate title={t('mobile_share_gate_title')} subtitle={t('mobile_share_gate_desc')} />
          <UnlockDialog open payload={payload} onUnlock={onUnlock} onClose={onClose} />
          <Toaster position="top-center" richColors closeButton />
        </>
      );
    }
    return (
      <>
        <MobileBlock />
        <Toaster position="top-center" richColors closeButton />
      </>
    );
  }

  // ---- Desktop: a "view only" share opens the read-only viewer ----
  if (status === 'viewing' && viewerDoc) {
    return (
      <>
        <ReadOnlyViewer doc={viewerDoc} />
        <Toaster position="top-right" richColors closeButton />
      </>
    );
  }

  // ---- Desktop: full editor (+ unlock dialog for shared links) ----
  return (
    <>
      <Editor />
      <UnlockDialog open={status === 'ready'} payload={payload} onUnlock={onUnlock} onClose={onClose} />
      <Toaster position="top-right" richColors closeButton />
    </>
  );
}

export default App;
